import { useCallback, useEffect, useState } from 'react';
import {
  fetchNotifications, fetchUnreadCount, markAllRead, markOneRead,
  type AdminNotification,
} from '../lib/notifications-api';

const POLL_INTERVAL_MS = 30_000;

interface UseAdminNotifications {
  items: AdminNotification[];
  unread: number;
  refresh: () => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markOneAsRead: (id: string) => Promise<void>;
}

/**
 * Poll the notifications endpoints every 30s. Provides refresh +
 * mark-read handlers so the drawer/bell don't each re-poll on action.
 * When the token is missing this no-ops (returns empty state).
 */
export function useAdminNotifications(token: string | null): UseAdminNotifications {
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async (): Promise<void> => {
    if (!token) return;
    const [next, count] = await Promise.all([
      fetchNotifications(token),
      fetchUnreadCount(token),
    ]);
    setItems(next);
    setUnread(count);
  }, [token]);

  useEffect(() => {
    void refresh();
    if (!token) return;
    const handle = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [token, refresh]);

  const markAllAsRead = useCallback(async (): Promise<void> => {
    if (!token) return;
    await markAllRead(token);
    await refresh();
  }, [token, refresh]);

  const markOneAsRead = useCallback(async (id: string): Promise<void> => {
    if (!token) return;
    await markOneRead(token, id);
    await refresh();
  }, [token, refresh]);

  return { items, unread, refresh, markAllAsRead, markOneAsRead };
}
